import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Truck, Factory, Check, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError, setAccessToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { defaultRoute } from "@/components/RoleRoute";
import { GFS_LOGO_URL } from "@/lib/brand";

const MATERIALS = ["PET", "HDPE", "HDPE(N)", "PP", "PP(N)", "PE(N)", "PE(M)", "UBC", "Mixed Plastics"];

export default function Signup() {
  const [role, setRole] = useState("collector");
  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="signup-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <Link
          to="/login"
          data-testid="back-to-login"
          className="inline-flex items-center gap-1.5 text-sm text-[#3F3F3F] hover:text-[#056839]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <img
            src={GFS_LOGO_URL}
            alt="GreenForest Solutions"
            className="w-10 h-10 object-contain"
          />
          <div className="leading-tight">
            <div className="font-heading text-2xl font-semibold">
              PRO<span className="text-[#056839]">cess</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#69A488]">
              Create your account
            </div>
          </div>
        </div>

        <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#000000] mt-6">
          Join the traceability network
        </h1>
        <p className="text-sm text-[#3F3F3F] mt-1">
          Choose how you participate in the packaging value chain.
        </p>

        {/* Role selector */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <RoleCard
            active={role === "collector"}
            onClick={() => setRole("collector")}
            icon={<Truck className="w-5 h-5" />}
            title="Collector"
            sub="Pick up packaging waste from sources and bring it to a facility."
            testId="role-collector"
          />
          <RoleCard
            active={role === "facility"}
            onClick={() => setRole("facility")}
            icon={<Factory className="w-5 h-5" />}
            title="Facility (MRF / Recycler)"
            sub="Receive deliveries, run processing and log shipments to destinations."
            testId="role-facility"
          />
          <RoleCard
            active={role === "viewer"}
            onClick={() => setRole("viewer")}
            icon={<Eye className="w-5 h-5" />}
            title="Viewer"
            sub="Read-only access to all channels for monitoring and reporting."
            testId="role-viewer"
          />
        </div>

        <div className="mt-6">
          {role === "collector" ? <CollectorForm />
           : role === "facility" ? <FacilityForm />
           : <ViewerForm />}
        </div>
      </div>
    </div>
  );
}

function RoleCard({ active, onClick, icon, title, sub, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 rounded-xl border-2 transition-all ${
        active
          ? "border-[#056839] bg-[#D2E4D6]/40 shadow-sm"
          : "border-[#E4D5B7] bg-white hover:border-[#69A488]"
      }`}
    >
      <div className="flex items-center gap-2">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${
            active ? "bg-[#056839] text-white" : "bg-[#D2E4D6] text-[#056839]"
          }`}
        >
          {icon}
        </div>
        <div className="font-heading font-semibold text-[#000000]">{title}</div>
      </div>
      <div className="text-xs text-[#3F3F3F]/80 mt-2 leading-snug">{sub}</div>
    </button>
  );
}

function FieldLabel({ children, required }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#69A488] font-medium">
      {children}
      {required && <span className="ml-1 text-[#A0522D] normal-case tracking-normal">*</span>}
    </Label>
  );
}

function CollectorForm() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [businessReg, setBusinessReg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!name.trim() || !email.trim() || !password) {
      setErr("Name, email and password are required");
      return;
    }
    if (!companyName.trim()) { setErr("Company name is required"); return; }
    if (!phone.trim())       { setErr("Phone is required"); return; }
    if (!address.trim())     { setErr("Address is required"); return; }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup/collector", {
        name: name.trim(),
        email: email.trim(),
        password,
        company_name: companyName.trim(),
        phone: phone.trim(),
        address: address.trim(),
        business_registration: businessReg.trim() || undefined,
      });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data);
      navigate(defaultRoute("collector", data.status), { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="collector-signup-form">
      <div>
        <FieldLabel required>Full name</FieldLabel>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          required
          data-testid="signup-name"
          className="mt-1 h-11"
        />
      </div>
      <div>
        <FieldLabel required>Company name</FieldLabel>
        <Input
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="EcoCollect SARL"
          required
          data-testid="signup-company"
          className="mt-1 h-11"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Email</FieldLabel>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            data-testid="signup-email"
            className="mt-1 h-11"
          />
        </div>
        <div>
          <FieldLabel required>Phone</FieldLabel>
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+33 6 12 34 56 78"
            required
            data-testid="signup-phone"
            className="mt-1 h-11"
          />
        </div>
      </div>
      <div>
        <FieldLabel required>Address</FieldLabel>
        <Input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="12 Rue de la République, 75001 Paris"
          required
          data-testid="signup-address"
          className="mt-1 h-11"
        />
      </div>
      <div>
        <FieldLabel>Business registration #</FieldLabel>
        <Input
          value={businessReg}
          onChange={(e) => setBusinessReg(e.target.value)}
          placeholder="SIRET / Reg. number (optional)"
          data-testid="signup-business-reg"
          className="mt-1 h-11"
        />
      </div>
      <div>
        <FieldLabel required>Password</FieldLabel>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Min. 8 characters"
          required
          data-testid="signup-password"
          className="mt-1 h-11"
        />
      </div>
      {err && (
        <div
          className="text-sm bg-[#A0522D]/10 text-[#A0522D] px-3 py-2 rounded-lg"
          data-testid="signup-error"
        >
          {err}
        </div>
      )}
      <Button
        type="submit"
        disabled={busy}
        data-testid="signup-submit"
        className="w-full h-12 gfs-bg-primary hover:bg-[#044E2A] text-white font-medium"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create collector account"}
      </Button>
    </form>
  );
}

function FacilityForm() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [managerName, setManagerName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [facilityName, setFacilityName] = useState("");
  const [address, setAddress] = useState("");
  const [registration, setRegistration] = useState("");
  const [employees, setEmployees] = useState("");
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function toggleMaterial(m) {
    setMaterials((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (
      !managerName.trim() ||
      !email.trim() ||
      !password ||
      !facilityName.trim() ||
      !address.trim() ||
      !registration.trim() ||
      !employees ||
      materials.length === 0
    ) {
      setErr("All required fields are needed (including at least one material)");
      return;
    }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    const empCount = parseInt(employees, 10);
    if (!empCount || empCount < 1) {
      setErr("Employees count must be at least 1");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup/facility", {
        name: managerName.trim(),
        email: email.trim(),
        password,
        phone: phone.trim() || undefined,
        facility_name: facilityName.trim(),
        address: address.trim(),
        registration_number: registration.trim(),
        materials_processed: materials,
        employees_count: empCount,
      });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data);
      navigate(defaultRoute("facility", data.status), { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="facility-signup-form">
      <Section title="Manager account">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel required>Manager name</FieldLabel>
            <Input
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              required
              data-testid="signup-name"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <FieldLabel>Phone</FieldLabel>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+33 1 23 45 67 89"
              data-testid="signup-phone"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <FieldLabel required>Email</FieldLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="signup-email"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <FieldLabel required>Password</FieldLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
              required
              data-testid="signup-password"
              className="mt-1 h-11"
            />
          </div>
        </div>
      </Section>

      <Section title="Facility details">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FieldLabel required>Facility name</FieldLabel>
              <Input
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                placeholder="EcoRecyclers Paris"
                required
                data-testid="signup-facility-name"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <FieldLabel required>Business registration #</FieldLabel>
              <Input
                value={registration}
                onChange={(e) => setRegistration(e.target.value)}
                placeholder="e.g., RCS Paris 123 456 789"
                required
                data-testid="signup-registration"
                className="mt-1 h-11"
              />
            </div>
          </div>
          <div>
            <FieldLabel required>Full address</FieldLabel>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              placeholder="Street, number, district, city, postcode"
              required
              data-testid="signup-address"
              className="mt-1 w-full rounded-lg border-2 border-[#E4D5B7] focus:border-[#056839] focus:outline-none focus:ring-2 focus:ring-[#056839]/15 px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <FieldLabel required>Number of employees</FieldLabel>
            <Input
              type="number"
              min="1"
              value={employees}
              onChange={(e) => setEmployees(e.target.value)}
              placeholder="e.g., 24"
              required
              data-testid="signup-employees"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <FieldLabel required>Materials processed</FieldLabel>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="signup-materials">
              {MATERIALS.map((m) => {
                const active = materials.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMaterial(m)}
                    data-testid={`signup-material-${m.toLowerCase().replace(/\s/g, "-")}`}
                    className={`relative h-11 rounded-lg border-2 px-3 text-sm font-medium transition-all ${
                      active
                        ? "border-[#056839] bg-[#D2E4D6] text-[#056839]"
                        : "border-[#E4D5B7] bg-white text-[#3F3F3F] hover:border-[#69A488]"
                    }`}
                  >
                    {active && (
                      <Check className="w-3.5 h-3.5 absolute top-1 right-1 text-[#056839]" />
                    )}
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {err && (
        <div
          className="text-sm bg-[#A0522D]/10 text-[#A0522D] px-3 py-2 rounded-lg"
          data-testid="signup-error"
        >
          {err}
        </div>
      )}
      <Button
        type="submit"
        disabled={busy}
        data-testid="signup-submit"
        className="w-full h-12 gfs-bg-primary hover:bg-[#044E2A] text-white font-medium"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create facility account"}
      </Button>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#D2E4D6]/70 rounded-xl p-5 md:p-6 shadow-sm">
      <div className="text-xs uppercase tracking-[0.18em] text-[#69A488] mb-3 font-medium">
        {title}
      </div>
      {children}
    </div>
  );
}


function ViewerForm() {
  return (
    <div
      className="rounded-xl border border-[#E4D5B7] bg-[#F9F8F6] p-5 text-sm text-[#3F3F3F]"
      data-testid="viewer-signup-note"
    >
      <p className="font-medium text-[#000000] mb-1">
        Viewer accounts are provisioned by an administrator.
      </p>
      <p>
        Please contact your admin to request a read-only viewer account. Once created,
        you&apos;ll receive credentials by email.
      </p>
    </div>
  );
}
